import React, { createRef, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { isNil } from "lodash";

import { Col, Input, Row, Space, Spin } from "antd";
import { Button, SkeletonLoader, Loader, TextInput } from "@life-line-lab/components-react";

import "./verify-account.scss";
import useQuery from "Hooks/query.hook";
import { useRecoilState } from "recoil";
import { authActionStatusAtom, AuthActionStatusAtom } from "Recoil/auth.atom";
import { RiderType } from "Types/new_driver_types";
import { UserRoleToAssign } from "Types/user.types";

interface VerifyAccount {
  status: "idle" | "loading" | "success" | "error";
  onSetVerifyToken: (input: {
    emailToken: string;
    key?: string;
    passToken?: string;
    email?: string;
    riderType?: RiderType;
    subType?: UserRoleToAssign;
    orgEmail?: string;
  }) => void;
  onResendEmail: () => void;
}

const VerifyAccount: React.FC<VerifyAccount> = ({ status, onSetVerifyToken, onResendEmail }) => {
  let query = useQuery();
  const history = useHistory();
  const { t } = useTranslation();

  let refs = useRef<any>([createRef(), createRef(), createRef(), createRef()]);

  const initialInput = {
    input1: "",
    input2: "",
    input3: "",
    input4: "",
  };
  const inputArr = [1, 2, 3, 4];

  const [message, setMessage] = useState<string>("");
  const [input, setInput] = useState(initialInput);
  const isLoading = status === "loading";
  const [, setStatus] = useRecoilState<AuthActionStatusAtom>(authActionStatusAtom);

  const [countDown, setCountDown] = useState(0);
  const [runTimer, setRunTimer] = useState(false);

  const emailToken = query.get("emailToken") || "";
  const passToken = query.get("passToken") || "";
  const key = query.get("key") || "";
  const email = query.get("email") || "";
  const riderType = (query.get("riderType") || null) as RiderType;
  const subType = (query.get("subType") || null) as UserRoleToAssign;
  const orgEmail = query.get("orgEmail") || "";

  useEffect(() => {
    if (emailToken) {
      onSetVerifyToken({ emailToken, passToken, key, riderType, subType, orgEmail });
    }
  }, []);

  useEffect(() => {
    if (checkIfInputComplete()) {
      setStatus((prevStatus) => ({ ...prevStatus, verifyEmail: "loading" }));
      let otp = "";
      for (let eachInput in input) {
        otp += input[eachInput];
      }
      onSetVerifyToken({ emailToken: otp, email, riderType, subType, orgEmail });
    }
  }, [input]);

  useEffect(() => {
    let timerId;

    if (runTimer) {
      setCountDown(60);
      timerId = setInterval(() => {
        setCountDown((countDown) => countDown - 1);
      }, 1000);
    } else {
      clearInterval(timerId);
    }

    return () => clearInterval(timerId);
  }, [runTimer]);

  useEffect(() => {
    if (countDown < 0 && runTimer) {
      console.log("expired");
      setRunTimer(false);
      setCountDown(0);
    }
  }, [countDown, runTimer]);

  const togglerTimer = () => setRunTimer((t) => !t);

  const checkIfInputComplete = () => {
    for (let eachInput in input) {
      if (input[eachInput].length === 0) {
        return false;
      }
    }
    return true;
  };

  const isPositiveInteger = (str) => {
    if (typeof str !== "string") {
      return false;
    }

    const num = Number(str);

    if (Number.isInteger(num) && num > 0) {
      return true;
    }

    return false;
  };

  const handleResendEmail = () => {
    if (!runTimer) {
      onResendEmail();
      togglerTimer();
    }
  };

  const handleFieldChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInput((prevInput) => ({
      ...prevInput,
      [e.target.name]: e.target.value,
    }));
    if (e.target.value.length > 0) {
      focusNextInput(e.target.name);
    }
  };

  const handleKeyDown = (e: any) => {
    if (e.ctrlKey || (e.metaKey && e.keyCode == 86)) {
      return false;
    } else {
      switch (e.key) {
        case "ArrowDown": {
          e.preventDefault();
          break;
        }
        case "ArrowUp": {
          e.preventDefault();
          break;
        }
        case "Backspace": {
          if (input[e.target.name].length > 0) {
            setInput((prevInput) => ({
              ...prevInput,
              [e.target.name]: "",
            }));
          } else {
            e.preventDefault();
            focusPrevInput(e.target.name);
          }
          break;
        }
        default: {
          if (!isFinite(e.key)) {
            e.preventDefault();
          }
          break;
        }
      }
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    if (e.currentTarget.name === "input1") {
      const clipboardData = e.clipboardData || (window as any).clipboardData;
      const pastedText = clipboardData.getData("text");

      if (isPositiveInteger(pastedText)) {
        const splitText = pastedText.split("");
        for (let i = 1; i < 5; i++) {
          setInput((prevInput) => ({
            ...prevInput,
            ["input" + i]: splitText[i - 1],
          }));
          focusNextInput("input" + i);
        }
      } else {
        e.preventDefault();
      }
    } else {
      e.preventDefault();
    }
  };

  const focusNextInput = (targetName: string) => {
    const inputPostion = parseInt(targetName.replace("input", ""));
    if (inputPostion < 3) {
      refs.current[inputPostion]!.current.blur();
      refs.current[inputPostion]!.current.focus();
    }
    if (inputPostion === 3) {
      refs.current[3]!.current.focus();
    }
  };

  const focusPrevInput = (targetName: string) => {
    const inputPostion = parseInt(targetName.replace("input", ""));
    if (inputPostion > 1) {
      refs.current[inputPostion - 2]!.current.focus({
        cursor: "all",
      });
      refs.current[inputPostion - 1]!.current.blur();
    }
    if (inputPostion === 1) {
      refs.current[0]!.current.focus({
        cursor: "all",
      });
      refs.current[1]!.current.blur();
    }
  };

  return (
    <div className="verify-account">
      <div className="view">
        <Spin spinning={isLoading}>
          <Space className="verify-account--inner" direction="vertical" size="large">
            <React.Fragment>
              <Space className="otp-group" direction="horizontal" size="small">
                {inputArr.map((inputNum) => {
                  return (
                    <Input
                      name={"input" + inputNum}
                      value={input["input" + inputNum]}
                      onChange={handleFieldChange}
                      maxLength={1}
                      className="otp-input"
                      onKeyDown={handleKeyDown}
                      ref={refs.current[inputNum - 1]}
                      onPaste={handlePaste}
                    />
                  );
                })}
              </Space>

              <div>
                <div dangerouslySetInnerHTML={{ __html: t("txt.otpExpiry") }} />
                <div>- {t("txt.didntReceiveCode")}</div>
                <div>
                  {countDown > 0 && runTimer ? (
                    <span className="ml-4">
                      {t(`txt.obtainCodeInfo`)} {countDown}
                    </span>
                  ) : (
                    <a onClick={() => handleResendEmail()} className="ml-12">
                      {t("action.resend")}
                    </a>
                  )}
                </div>
              </div>
            </React.Fragment>
          </Space>
        </Spin>
      </div>
    </div>
  );
};

export default VerifyAccount;
